import { create } from 'zustand'

import {
  acceptPolicyApi,
  airdropdApi,
  authUserApi,
  ChannelSubscription,
  checkChannelSubscription,
  ClaimApi,
  claimRewardByUUID,
  completePrologueApi,
  donateRewardApi,
  fetchProfileApi,
  getNotClaimAvailabilityApi,
  getSubscriptionStatus,
  joinUserApi,
  SubscriptionStatus,
} from '@api'
import config from '@config'
import { AuthService } from '@services'
import {
  isNotCoinClaimAvailable,
  isRechargeDropAvailable,
  setNotCoinClaimDone,
  setRechargeDropDone,
} from '@shared'

import { createSelectors, Mapper } from './helpers'
import { MainInventoryBackendCard } from './inventory'

export interface NotClaimAvailabilityBackend {
  all_quests_solved: boolean
  battlepass_level_reached: boolean
  is_available: boolean
  is_wallet_connected: boolean
  score_reached: boolean
  is_donated: boolean
  balance: number
  is_suspended: boolean
}

export interface NotClaimAvailability {
  allQuestsSolved: boolean
  battlepassLevelReached: boolean
  isAvailable: boolean
  isWalletConnected: boolean
  scoreReached: boolean
  isDonated: boolean
  balance: number
  isSuspended: boolean
}

const NotClaimAvailabilityMapper: Mapper<
  NotClaimAvailabilityBackend,
  NotClaimAvailability
> = {
  mapFromBackend: (data) => ({
    allQuestsSolved: data.all_quests_solved,
    battlepassLevelReached: data.battlepass_level_reached,
    isAvailable: data.is_available,
    isWalletConnected: data.is_wallet_connected,
    scoreReached: data.score_reached,
    isDonated: data.is_donated,
    balance: data.balance,
    isSuspended: data.is_suspended
  }),
  mapToBackend: (data) => ({
    all_quests_solved: data.allQuestsSolved,
    battlepass_level_reached: data.battlepassLevelReached,
    is_available: data.isAvailable,
    is_wallet_connected: data.isWalletConnected,
    score_reached: data.scoreReached,
    is_donated: data.isDonated,
    balance: data.balance,
    is_suspended: data.isSuspended
  }),
}

export interface UserProfileBackend {
  id: number
  username: string
  first_name: string
  last_name: string
  is_premium: boolean
  photo_url: string
  seed: number
  paid_battlepass: boolean
  battlepass_level: number
  passed_prologue: boolean
  skin_avatar_item_id: string
  skin_spiral_item_id: string
  skin_stars_item_id: string
  skin_avatar_effect_id: string
  created_at: string
  is_policy_accepted: boolean
  avatar_icon: string
  avatar_effect: string
  rating: number
}

export interface UserProfile {
  id: number
  username: string
  firstName: string
  lastName: string
  isPremium: boolean
  photoUrl: string
  seed: number
  paidBattlepass: boolean
  battlepassLevel: number
  passedPrologue: boolean
  skinAvatarItemId: string
  skinSpiralItemId: string
  skinStarsItemId: string
  skinAvatarEffectId: string
  createdAt: string
  isPolicyAccepted: boolean
  avatarIcon: string
  avatarEffect: string
  rating: number
}

const UserProfileMapper: Mapper<UserProfileBackend, UserProfile> = {
  mapFromBackend: function (data) {
    return {
      id: data.id,
      username: data.username,
      firstName: data.first_name,
      lastName: data.last_name,
      isPremium: data.is_premium,
      photoUrl: data.photo_url,
      seed: data.seed,
      isPolicyAccepted: data.is_policy_accepted,
      paidBattlepass: data.paid_battlepass,
      battlepassLevel: data.battlepass_level,
      passedPrologue: data.passed_prologue,
      skinAvatarItemId: data.skin_avatar_item_id,
      skinSpiralItemId: data.skin_spiral_item_id,
      skinStarsItemId: data.skin_stars_item_id,
      skinAvatarEffectId: data.skin_avatar_effect_id,
      createdAt: data.created_at,
      avatarIcon: data.avatar_icon,
      avatarEffect: data.avatar_effect,
      rating: data.rating,
    }
  },
  mapToBackend(data) {
    return {
      id: data.id,
      username: data.username,
      first_name: data.firstName,
      last_name: data.lastName,
      is_premium: data.isPremium,
      photo_url: data.photoUrl,
      seed: data.seed,
      is_policy_accepted: data.isPolicyAccepted,
      paid_battlepass: data.paidBattlepass,
      battlepass_level: data.battlepassLevel,
      passed_prologue: data.passedPrologue,
      skin_avatar_item_id: data.skinAvatarItemId,
      skin_spiral_item_id: data.skinSpiralItemId,
      skin_stars_item_id: data.skinStarsItemId,
      skin_avatar_effect_id: data.skinAvatarEffectId,
      created_at: data.createdAt,
      avatar_icon: data.avatarIcon,
      avatar_effect: data.avatarEffect,
      rating: data.rating,
    }
  },
}

interface UserState {
  isAuth: boolean
  isProfileFetching: boolean
  avatarUrlCached: string
  profile: UserProfile | null | undefined
  notClaimAvailability: NotClaimAvailability | null
}

interface UserAction {
  actions: {
    authorise(referrer?: number): Promise<void>
    join(refLink?: string): Promise<any>
    fetchProfile(): Promise<UserProfile | null>
    completePrologue(): Promise<any>
    claimNotReward(): Promise<MainInventoryBackendCard | undefined>
    acceptPolicy(): Promise<{ ok: boolean }>
    claimRechargeDrop(): Promise<MainInventoryBackendCard | undefined>
    checkChannelSubscription(): Promise<ChannelSubscription | null>
    getSubscriptionStatus(): Promise<SubscriptionStatus | null>
    getNotClaimAvailability(): Promise<void>
    donateReward(): Promise<void>
    airdrop(): Promise<ClaimApi | null>
    checkIfClaimed(): Promise<boolean>
  }
}

const useUserStoreBase = create<UserState & UserAction>((set, get) => ({
  profile: null,
  isAuth: config.isDev ? AuthService.isAuth() : false,
  isProfileFetching: false,
  avatarUrlCached: '',
  notClaimAvailability: null,
  actions: {
    authorise: async (referrer?: number) => {
      const { ok, data } = await authUserApi(referrer)

      if (ok && data) {
        AuthService.setCredentials({ accessToken: data })

        set(() => ({ isAuth: true }))
      }
    },
    join: async (refLink?: string) => {
      const { data } = await joinUserApi(refLink)

      if (data && data.ok !== false) {
        set(() => ({ profile: data as UserProfile }))
      } else if (data.ok === false) {
        set(() => ({ profile: undefined }))
      }
    },
    fetchProfile: async () => {
      set(() => ({ isProfileFetching: true }))
      const { ok, data } = await fetchProfileApi()
      if (data?.avatar_icon) {
        const avatarBlob = await fetch(data.avatar_icon).then((r) => r.blob())
        const avatarUrlCached = URL.createObjectURL(avatarBlob)
        set(() => ({ avatarUrlCached }))
      }
      if (data && ok !== false) {
        set(() => ({ profile: UserProfileMapper.mapFromBackend(data) }))
      }

      set(() => ({ isProfileFetching: false }))

      return (data && UserProfileMapper.mapFromBackend(data)) || null
    },
    completePrologue: async () => {
      const { ok, data } = await completePrologueApi()

      if (data && ok !== false) {
        set((store) => ({
          profile: { ...store.profile!, isPolicyAccepted: true },
        }))
      }
    },
    acceptPolicy: async (): Promise<{ ok: boolean }> => {
      const { ok, data } = await acceptPolicyApi()

      if (data && ok !== false) {
        set((state) => ({
          profile: { ...state.profile!, isPolicyAccepted: true },
        }))
      }
      return { ok }
    },
    claimNotReward: async () => {
      if (!isNotCoinClaimAvailable()) {
        return
      }
      const { ok, data, error } = await claimRewardByUUID(
        '019cc217-b11c-783c-a4ea-4318157ed200'
      )
      if (error === 'bad_request' || (data && ok)) {
        setNotCoinClaimDone()
      }
      return data
    },
    claimRechargeDrop: async () => {
      if (!isRechargeDropAvailable()) {
        return
      }
      const { ok, data, error } = await claimRewardByUUID(
        '019d2946-ede3-7101-a86b-770978bacaca'
      )
      if (error === 'bad_request' || (data && ok)) {
        setRechargeDropDone()
      }
      return data
    },
    checkChannelSubscription: async () => {
      const { ok, data } = await checkChannelSubscription();
      if (ok && data) {
        return data;
      }
      return null;
    },
    getSubscriptionStatus: async () => {
      const { ok, data } = await getSubscriptionStatus();
      if (ok && data) {
        return data;
      }
      return null;
    },
    getNotClaimAvailability: async () => {
      const { ok, data } = await getNotClaimAvailabilityApi()
      if (ok && data) {
        set(() => ({
          notClaimAvailability: NotClaimAvailabilityMapper.mapFromBackend(data),
        }))
      }
    },
    donateReward: async () => {
      await donateRewardApi();
      await get().actions.getNotClaimAvailability();
    },
    airdrop: async () => {
      const { data } = await airdropdApi();
      return data || null;
    },
    checkIfClaimed: async () => {
      const { error_code } = await airdropdApi();
      return error_code === 'user_limit_reached'
    },
  },
}))

export const useUserActions = () => useUserStoreBase((state) => state.actions)
export const useUserState = () => useUserStoreBase((state) => state.profile)
export const useIsUserProfileFetching = () =>
  useUserStoreBase((state) => state.isProfileFetching)

export const useUserSpiralId = () =>
  useUserStoreBase((state) => state.profile?.skinSpiralItemId)

export const useUserStarId = () =>
  useUserStoreBase((state) => state.profile?.skinStarsItemId)

export const useUserAvatarId = () =>
  useUserStoreBase((state) => state.profile?.skinAvatarItemId)

export const useUserAvatarEffectId = () =>
  useUserStoreBase((state) => state.profile?.skinAvatarEffectId)

export const useNotClaimAvailability = () =>
  useUserStoreBase((state) => state.notClaimAvailability)

export const useUser = createSelectors(useUserStoreBase)

export function getUserObject() {
  const raw = window?.Telegram?.WebApp.initDataUnsafe.user

  if (!raw) {
    return {
      id: '211105588',
      name: 'M D',
    }
  }

  return {
    id: raw.id,
    name: raw.first_name ? `${raw.first_name} ${raw.last_name}` : raw.username,
    photoUrl: raw.photo_url,
  }
}
